import { Button, IColumnHeader, Icon, SearchBolder, Switch, TableColumn, TableRow } from '@aon/aon-tws-common-ui';
import React, { useState } from 'react';
import { useAppDispatch } from '../../../../appStore';
import { ISurveyQuestionEditModel, SeverityType } from '../../../../models';
import { toggleResponseIsRequired } from '../../../../store/SurveyQuestionsAdminSlice';
import { assertNonNullable } from '../../../../utilities/assertions';
import { WarningBox } from '../../../common/warnings';

interface IQuestionsTableRowProps {
	columns: IColumnHeader[];
	errors?: string[];
	followUpQuestion?: JSX.Element;
	isSurveyClosed: boolean;
	question: ISurveyQuestionEditModel;
	searchText: string;
	warnings?: string[];
	editTranslations: (surveyQuestionId: number) => void;
}

export const QuestionsTableRow = ({
	columns,
	errors,
	followUpQuestion,
	isSurveyClosed,
	question,
	searchText,
	warnings,
	editTranslations,
}: IQuestionsTableRowProps) => {
	const dispatch = useAppDispatch();
	const [drawerState, updateDrawerState] = useState({ open: true });

	const getDrawerIcon = () => {
		if (!followUpQuestion) {
			return <></>;
		} else {
			return <Icon name={drawerState.open ? 'angle-up' : 'angle-down'} onClick={handleDrawerToggle} />;
		}
	};

	const getExpandedContent = () => {
		if (!followUpQuestion) {
			return undefined;
		}
		return <TableColumn colSpan={columns.length}>{followUpQuestion}</TableColumn>;
	};

	const handleDrawerToggle = () => {
		updateDrawerState({ open: !drawerState.open });
	};

	return (
		<TableRow expanded={drawerState.open} expandedRowRender={getExpandedContent}>
			{columns.map((column: IColumnHeader) => (
				<td key={`${question.surveyQuestionId}-${column.field}`}>
					{{
						text: (
							<>
								<SearchBolder searchText={searchText} text={question.text} />
								{errors !== undefined && errors.length > 0 && <WarningBox severity={SeverityType.Error} messages={errors} />}
								{warnings !== undefined && warnings.length > 0 && <WarningBox severity={SeverityType.Warning} messages={warnings} />}
							</>
						),
						isRequired: (
							<Switch
								text={<span className='sr-only'>Response Required for Question {question.code}</span>}
								checked={question.isRequired}
								disabled={isSurveyClosed}
								onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
									dispatch(
										toggleResponseIsRequired({
											surveyQuestionId: question.surveyQuestionId,
											isRequired: event.target.checked,
										}),
									)
								}
							/>
						),
						followUpQuestion: getDrawerIcon(),
						editTranslations: (
							<Button
								id={`edit-translation-btn-${question.surveyQuestionId}`}
								color='transparent'
								onClick={() => {
									assertNonNullable(question.surveyQuestionId);
									editTranslations(question.surveyQuestionId);
								}}
								size='md'
								disabled={false}
								aria-label={`Edit Translations for Question ${question.code}`}
							>
								<Icon name='edit' />
							</Button>
						),
					}[column.field] || <SearchBolder searchText={searchText} text={question[column.field]} />}
				</td>
			))}
		</TableRow>
	);
};
